import React, { useEffect, useState } from "react";
import { Bars3Icon } from "@heroicons/react/24/solid";
import './Blindtimer.less';
import { ReactComponent as PPLogo } from '../assets/images/pp-logo.svg';
import { ReactComponent as BlindTimerLogo } from '../assets/images/blindtimer-logo.svg';
import TimerDisplay from "../components/timer/TimerDisplay";
import TimerControls from "../components/timer/TimerControls";
import UserAuthModal from "../components/UserAuthModal";
import ConfigListModal from "../components/config/ConfigListModal";
import ThemeSwitchModal from "../components/ThemeSwitchModal";
import BetaDisclaimerModal from "../components/disclaimer/BetaDisclaimerModal";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { loadDefaultConfig } from "../slices/configSlice";

export default function Blindtimer() {
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const [userToken, setUserToken] = useState<string | null>(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showConfigsModal, setShowConfigsModal] = useState(false);
    const [showThemeModal, setShowThemeModal] = useState(false);

    const [isRunning, setIsRunning] = useState(false);
    const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const [total, setTotal] = useState(0);

    const config = useSelector((state: RootState) => state.config.activeConfig);

    useEffect(() => {
        const token = localStorage.getItem('userToken');
        if (token) setUserToken(token);

        if (!config) {
            dispatch(loadDefaultConfig());
        }
    }, [config]);

    useEffect(() => {
        if (config && config.blinds[currentLevelIndex]) {
            setTimeLeft(config.blinds[currentLevelIndex].duration * 60);
        }
    }, [config, currentLevelIndex]);

    useEffect(() => {
        if (!isRunning) return;

        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    if (config && currentLevelIndex < config.blinds.length - 1) {
                        setCurrentLevelIndex(prevIndex => prevIndex + 1);
                        return config.blinds[currentLevelIndex + 1].duration * 60;
                    } else {
                        setIsRunning(false);
                        return 0;
                    }
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isRunning, config, currentLevelIndex]);

    useEffect(() => {
        if (!config || !config.blinds[currentLevelIndex]) return;

        setTotal(config.blinds[currentLevelIndex].duration * 60);
        const progress = ((total - timeLeft) / total) * 100;

        document.documentElement.style.setProperty('--progress', `${progress}%`);
        document.documentElement.style.setProperty('--progress-color', 'var(--highlight)');
    }, [timeLeft, currentLevelIndex, config]);

    const handleLogout = () => {
        localStorage.removeItem('userToken');
        setUserToken(null);
        setMenuOpen(false);
    };

    const handleNextLevel = () => {
        if (config && currentLevelIndex < config.blinds.length - 1) {
            setCurrentLevelIndex(prev => prev + 1);
        }
    };

    const handlePrevLevel = () => {
        if (currentLevelIndex > 0) {
            setCurrentLevelIndex(prev => prev - 1);
        }
    };

    const handleAddMinute = () => {
        const max =
            total || (config?.blinds[currentLevelIndex]?.duration ?? 0) * 60;
        setTimeLeft(prev => Math.min(max, prev + 60));
    };

    const handleSubtractMinute = () => {
        setTimeLeft((prev) => Math.max(0, prev - 60));
    };

    return (
        <>
            {/* Hamburger Icon */}
            <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="absolute top-4 left-4 z-10"
            >
                <Bars3Icon className="w-8 h-8 text-white" />
            </button>

            {/* Timer Controls */}
            <TimerControls
                isRunning={isRunning}
                onStartPause={() => setIsRunning((prev) => !prev)}
                onPrevLevel={handlePrevLevel}
                onNextLevel={handleNextLevel}
                onAddMinute={handleAddMinute}
                onSubtractMinute={handleSubtractMinute}
            />

            {/* Flyout Menu */}
            {menuOpen && (
                <div className="flyout absolute top-0 left-0 w-64 h-full z-20 flex flex-col p-6 shadow-lg">
                    <div className="close-menu flex justify-end">
                        <button
                            onClick={() => setMenuOpen(false)}
                            className="text-2xl font-bold"
                            aria-label="Menü schließen"
                        >
                            &times;
                        </button>
                    </div>
                    <div className="menu space-y-4">
                        {!userToken ? (
                            <p onClick={() => setShowUserModal(true)} className="text-lg font-semibold cursor-pointer">
                                Login / Create Account
                            </p>
                        ) : (
                            <>
                                <p
                                    className="text-lg font-semibold cursor-pointer"
                                    onClick={() => {
                                        setShowConfigsModal(true);
                                        setMenuOpen(false);
                                    }}
                                >
                                    Meine Configs
                                </p>

                                <p
                                    className="text-lg font-semibold cursor-pointer"
                                    onClick={() => {
                                        setShowThemeModal(true);
                                        setMenuOpen(false);
                                    }}
                                >
                                    Design wechseln
                                </p>

                                <p
                                    className="text-lg font-semibold cursor-pointer"
                                    onClick={handleLogout}
                                >
                                    Logout
                                </p>
                            </>
                        )}
                    </div>
                    <div className="bottom-menu space-y-4">
                        <a
                            href="https://powerpoker.shop/policies/legal-notice"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm"
                        >
                            Impressum
                        </a>

                        <a
                            href="https://powerpoker.shop/policies/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm"
                        >
                            Datenschutzerklärung
                        </a>
                    </div>
                </div>
            )}

            {/* Timer UI */}
            <TimerDisplay currentLevelIndex={currentLevelIndex} timeLeft={timeLeft} levelDuration={total} />

            {/* Auth Modal */}
            <UserAuthModal
                isOpen={showUserModal}
                onClose={() => setShowUserModal(false)}
                onSuccess={(token) => {
                    localStorage.setItem('userToken', token);
                    setUserToken(token);
                    setShowUserModal(false);
                }}
            />

            {/* Configs Modal */}
            <ConfigListModal
                isOpen={showConfigsModal}
                onClose={() => setShowConfigsModal(false)}
            />

            {/* Theme Modal */}
            <ThemeSwitchModal
                isOpen={showThemeModal}
                onClose={() => setShowThemeModal(false)}
            />

            <div className="footer">
                <div className="footer-left">
                    <div className="alternative-logo"></div>
                </div>
                <div className="footer-center" style={{ color: 'var(--accent)' }}>
                    <BlindTimerLogo />
                </div>
                <div className="footer-right" style={{ color: 'var(--accent)' }}>
                    <PPLogo  />
                </div>
            </div>

            <BetaDisclaimerModal />
        </>

    );
}
